/**
 * The dashboard's `ApiClient` — the one HTTP brain for every feature.
 *
 * Wires region routing + minute-based failover, the `Source-Platform` /
 * `Authorization` headers, the short GET cache and the catalogued error model
 * from `@authe/fe-commons/api`. The bearer token is pulled live from the
 * `SessionManager` (refreshed by the Auth0 SDK as needed), so the client never
 * holds a stale token.
 */
import { ApiClient } from "@authe/fe-commons/api";
import { makePathRewriter, RESOURCE_REWRITES } from "@authe/fe-commons/config";
import { requestKeyHeaders, twoKeyRequestHeaders } from "@authe/fe-commons/signing";
import { recordApiResponse } from "@authe/ui-core/observability";
import { appConfig, APPLICATION_SLUG, APPLICATION_AUTH_TYPE } from "@/config";
import { ensureDeviceEnrolment, getReadyDeviceSigner } from "./device-signing";
import { regionRoutingConfig } from "./routing-config";

/**
 * Anti-replay + device-bound request signer. Emits TWO overlapping schemes so
 * the report_only validation window can prove ECDSA two-key without dropping
 * the live REQUEST-KEY protection:
 *
 *  - REQUEST-KEY (always): signs `version:timestamp:nonce` with the obfuscated
 *    build-injected keyring plus a single-use REQUEST-NONCE. The private
 *    authorizer verifies the key against the keyring and claims the nonce once,
 *    so a replay is denied. Symmetric ⇒ anti-abuse, not authentication.
 *  - Two-key `X-Auth-*` (once the device is enrolled): the device root key
 *    certifies a short-lived intermediate that signs the SAME path-independent
 *    `{nonce,timestamp}` payload. The private key never leaves the browser, so
 *    this is true per-device/per-user auth. Absent a ready signer we fire
 *    lazy enrolment in the background (best-effort — never blocks the request)
 *    and simply omit `X-Auth-*` this call; report_only never denies.
 *
 * Both messages are PATH-INDEPENDENT (the same contract as GraphQL/public): the
 * gateway strips the stage + `/private/<service>-api` prefix and injects the
 * verb/colour/region into the backend's `method_path`, so the URL can't be
 * reproduced server-side and is deliberately not signed.
 */
async function signRequest(): Promise<Record<string, string>> {
  const headers = requestKeyHeaders({ url: "" });
  const signer = getReadyDeviceSigner();
  if (signer) {
    Object.assign(headers, await twoKeyRequestHeaders(signer));
  } else {
    void ensureDeviceEnrolment();
  }
  return headers;
}

/**
 * Dashboard-local rewrite table: the shared `RESOURCE_REWRITES` doesn't yet
 * carry `subscriptions-mgmt-api` or the `permissions` org-user surface, so we
 * prepend those rules (first-win) and fall back to the shared rules for
 * everything else.
 *
 * - subscriptions-mgmt-api mounts on the `.management.` zone at
 *   `management/subscriptions-mgmt-api`, so canonical `/subscriptions[/{id}]`
 *   maps to `/management/subscriptions-mgmt-api/v0/...`.
 * - permissions-mgmt-api mounts on the `.management.` zone at
 *   `management/permissions`; the org-user + org-role surface is addressed via
 *   the distinct FE prefixes `/org-users` and `/org-roles` (so they never clash
 *   with management-api's own `/users`). `/org-users` → `/management/permissions
 *   /v0/users` covers the members list (`GET /org-users`) and the invite (`POST
 *   /org-users/invite`); `/org-roles` → `/management/permissions/v0/roles` backs
 *   the org role picker.
 *
 * The region router then injects {VERB}/{CONTINENT} and the `.management.` host.
 * Long-term these rules belong in `@authe/fe-commons` `resource-paths.ts`.
 */
const rewritePath = makePathRewriter({
  rules: [
    {
      match: "/subscriptions",
      to: "/management/subscriptions-mgmt-api/v0/subscriptions",
      note: "subscriptions-mgmt-api (.management. zone)",
    },
    {
      match: "/org-users",
      to: "/management/permissions/v0/users",
      note: "permissions-mgmt-api org users + invite (.management. zone)",
    },
    {
      match: "/org-roles",
      to: "/management/permissions/v0/roles",
      note: "permissions-mgmt-api org role catalog (.management. zone)",
    },
    {
      // The org switcher: `/my-organizations` lists the caller's own
      // memberships and `/active-organization` moves their home tenancy. Both
      // sit under permissions' `identity` surface, distinct from
      // management-api's `/organizations` (which is the customer's org admin
      // view, a different question).
      match: "/my-organizations",
      to: "/management/permissions/v0/identity/my-organizations",
      note: "permissions-mgmt-api org switcher feed (.management. zone)",
    },
    {
      match: "/active-organization",
      to: "/management/permissions/v0/identity/active-organization",
      note: "permissions-mgmt-api org switch (.management. zone)",
    },
    {
      match: "/active-tenant",
      to: "/management/permissions/v0/identity/active-tenant",
      note: "permissions-mgmt-api tenant switch (.management. zone)",
    },
    ...RESOURCE_REWRITES.rules,
  ],
});

export function createApiClient(
  getAccessToken: () => Promise<string | null>,
  /**
   * Recover from a gateway 401/403 token-expiry rejection: refresh the bearer
   * (and update whatever the router reads) → return `true` to retry; or kick
   * off a re-login → return `false`. See {@link ApiClient.onAuthError}.
   */
  onAuthError?: () => Promise<boolean>,
): ApiClient {
  return new ApiClient({
    // Fallback base used only until the session token lands (and in the rare
    // token-without-region_config case). The CloudFront `api.` edge is
    // same-origin in prod; real calls route off it the moment the token loads.
    baseUrl: appConfig.apiUrl,
    backupBaseUrl: appConfig.backupApiUrl,
    region: appConfig.region,
    application: APPLICATION_SLUG,
    authType: APPLICATION_AUTH_TYPE,
    getAccessToken,
    requestSigner: signRequest,
    ...(onAuthError ? { onAuthError } : {}),
    // Map canonical FE paths (`/transactions/blockchain`, …) onto the backend's
    // tier-scoped base paths (`/private/transactions/v0/blockchain`). The region
    // router below then injects the verb + continent and the regional host.
    rewritePath,
    // Regional `.api.` routing (shared brain): resolves every call to
    // `{alias}.{continent}.api.{domain}` from the JWT, injects `{VERB}/{CONTINENT}`
    // into the version segment and the blue/green colour, and does the legacy
    // minute-based failover — instead of the `api.` CloudFront edge (which has no
    // CORS for localhost). e.g. POST /transactions/blockchain →
    // https://pdx.us.api.sandbox.authe.io/private/transactions/blue/ANY/US/v0/transactions/blockchain
    //
    // Shared with the GraphQL client (see `routing-config.ts`) so REST and
    // GraphQL region-switch and fail over together off the same JWT snapshot.
    routing: regionRoutingConfig,
    regionApiRouting: true,
    onResponse: recordApiResponse,
  });
}
